// `/embed/extension` — the page the Chrome extension's side panel frames (W8, GRO-110).
//
// The side panel is a `chrome-extension://` page; this is the dashboard itself inside it, signed
// in with its own Clerk session when it has one and otherwise with the extension's token
// (`extension/frameToken.ts`, via `auth/AuthContext.tsx`), so it shares the Scout tab's chat runtime
// (`ChatProvider`, threads, approval cards) and ships with each dashboard deploy rather than
// with an extension reload. The side panel reads the open tab (on open, on a tab switch or a
// navigation, and on "Read page") and posts the result here (`extension/bridge.ts`); this page
// resolves the company against the book and shows a one-line identity strip, then the sidebar
// redesign's four tabs (Phase 4, item 6):
//
// - Scout: the chat, with the page passed as the turn's context (TurnView), labelled untrusted.
// - Company: best signal, overview, ownership; off the book, the Salesforce record.
// - Outreach: the person on a LinkedIn or Sales Nav page, the Gmail reply, then heroes and people.
// - Research: Scout's research and the meeting and deal history.
//
// Every tab stays mounted, so an enrichment, a draft or a streaming answer survives a tab switch.
// No view-as here: `readViewAs` returns null on /embed/*, and the server ignores view-as on
// `/api/extension/*`.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { AccessDeniedError, fetchBook, fetchFunding, fetchMe, panelMessage } from '../api/client';
import type { BookAccount, BookPayload, EnrichCandidate, ExtensionPersonAnswer } from '../api/types';
import { applyEnrichedPerson } from '../lib/enrichedBook';
import { useAuthContext } from '../auth/AuthContext';
import { attachFunding } from '../book/funding';
import { AccountStrip, CompanySection, OutreachAccount, ResearchSection } from '../extension/AccountSections';
import { pageContextFromEvent, postToExtension } from '../extension/bridge.ts';
import type { PageContext } from '../extension/bridge.ts';
import {
  PANEL_TABS, TOOL_SITE_LINE, companyHint, defaultTabFor, isPersonPage, isToolSite, personCompanyBlock, personDomainFor, resolveAccount,
  startersFor,
  turnViewFor,
} from '../extension/pageContext.ts';
import type { PanelTab } from '../extension/pageContext.ts';
import { PersonTab } from '../extension/PersonTab';
import { ReplyTab } from '../extension/ReplyTab';
import { ChatPanel } from '../sidebar/ChatEmbed';
import { CollapseProvider } from '../sidebar/Collapsible';

const TAB_LABELS: Record<PanelTab, string> = { scout: 'Scout', company: 'Company', outreach: 'Outreach', research: 'Research' };
const CHAT_STORAGE_PREFIX = 'scout-extension-chat:';

// One Scout conversation per company off the book, kept across panel opens.
function conversationFor(key: string): string {
  const storageKey = CHAT_STORAGE_PREFIX + (key || 'no-company');
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored) return stored;
    const created = crypto.randomUUID();
    window.localStorage.setItem(storageKey, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

function contextLine(context: PageContext | null): string {
  if (!context) return 'Waiting for the page…';
  if (isPersonPage(context)) {
    return [context.person_name, context.person_title, context.person_company].filter(Boolean).join(' · ') || 'A LinkedIn profile';
  }
  if (context.kind === 'linkedin_company' || context.kind === 'sales_nav_company') return context.company_name || 'A LinkedIn company page';
  if (context.kind === 'gmail_thread') return context.email_subject || 'A Gmail thread';
  return context.host || context.title || 'This page';
}

// Arrow keys, Home and End move between the four tabs, as in the sidebar.
function neighbourTab(key: string, current: PanelTab): PanelTab | null {
  const index = PANEL_TABS.indexOf(current);
  const count = PANEL_TABS.length;
  if (key === 'ArrowRight') return PANEL_TABS[(index + 1) % count]!;
  if (key === 'ArrowLeft') return PANEL_TABS[(index + count - 1) % count]!;
  if (key === 'Home') return PANEL_TABS[0];
  if (key === 'End') return PANEL_TABS[count - 1]!;
  return null;
}


// How long after an accepted Find the extension re-reads the book.
const BOOK_REREAD_AFTER_FIND_MS = 3000;

export function ExtensionEmbedView() {
  const { getToken, email } = useAuthContext();
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const bookQuery = useQuery({ queryKey: ['book'], queryFn: () => fetchBook(getToken) });

  // An accepted Find shows at once (the cached book is patched), then the book is re-read so the
  // page matches the server. The served book only names the person at its next rebuild, so the
  // accepts made in this panel are laid on top of the re-read again rather than lost.
  const acceptedFinds = useRef<{ contactId: string; candidate: EnrichCandidate }[]>([]);
  function withAcceptedFinds(current: BookPayload | undefined): BookPayload | undefined {
    if (!current) return current;
    return acceptedFinds.current.reduce((book, accepted) => applyEnrichedPerson(book, accepted.contactId, accepted.candidate), current);
  }
  function onContactEnriched(contactId: string, candidate: EnrichCandidate) {
    acceptedFinds.current = [...acceptedFinds.current, { contactId, candidate }];
    queryClient.setQueryData<BookPayload>(['book'], withAcceptedFinds);
    window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ['book'] })
        .then(() => queryClient.setQueryData<BookPayload>(['book'], withAcceptedFinds));
    }, BOOK_REREAD_AFTER_FIND_MS);
  }
  // A person Enrich saved and listed is on the served book now (scout_found_contacts), so the
  // account's Outreach list re-reads it at once; the panel's accepted Finds stay laid on top.
  function onPersonListed() {
    void queryClient.invalidateQueries({ queryKey: ['book'] })
      .then(() => queryClient.setQueryData<BookPayload>(['book'], withAcceptedFinds));
  }
  const fundingQuery = useQuery({ queryKey: ['funding'], queryFn: () => fetchFunding(getToken) });
  const [context, setContext] = useState<PageContext | null>(null);
  const [typed, setTyped] = useState('');
  const [typedDraft, setTypedDraft] = useState('');
  const [tab, setTab] = useState<PanelTab>('company');
  const [personAnswer, setPersonAnswer] = useState<ExtensionPersonAnswer | null>(null);
  const [scoutVisited, setScoutVisited] = useState(false);
  useEffect(() => { if (tab === 'scout') setScoutVisited(true); }, [tab]);

  // Tell the side panel who is signed in; it answers with the page it read.
  const viewer = meQuery.data?.viewer;
  useEffect(() => {
    if (meQuery.error instanceof AccessDeniedError) postToExtension({ type: 'scout:embed-status', state: 'denied' });
    else if (viewer) postToExtension({ type: 'scout:embed-status', state: 'signed_in', email });
  }, [viewer, meQuery.error, email]);

  // The page context, only from the side panel that frames us. The panel re-reads on its own
  // when a tab loads or changes, so a second read of the same page keeps the tab and the typed
  // company; only a new page resets them.
  const lastUrl = useRef<string | null>(null);
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const parsed = pageContextFromEvent(event, window.parent);
      if (!parsed) return;
      setContext(parsed);
      if (parsed.url && parsed.url === lastUrl.current) return;
      lastUrl.current = parsed.url;
      setTyped('');
      setTypedDraft('');
      setTab(defaultTabFor(parsed));
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // The company on the page, as a book row when it is one.
  const accounts: BookAccount[] = useMemo(
    () => attachFunding(bookQuery.data?.accounts ?? [], fundingQuery.data),
    [bookQuery.data, fundingQuery.data],
  );
  const hint = companyHint(context, typed);
  // A tool's own site with nothing typed: the quiet state, not a lookup.
  const toolSite = isToolSite(context) && !typed.trim();
  const match = resolveAccount(accounts, hint);
  const row = match.account;
  const resolvedDomain = row?.domain || hint.domain;
  // The person lookup gets the account only when the page proved it, never by name alone.
  const personDomain = personDomainFor(match, hint);
  // What Enrich said about the person on this page; it can take the account's push away.
  const personBlock = isPersonPage(context) ? personCompanyBlock(personAnswer, row) : null;

  if (meQuery.error instanceof AccessDeniedError) {
    return <div className="embedgate"><p>{meQuery.error.message}</p></div>;
  }
  if (meQuery.isPending || !viewer) return <div className="embedgate"><p>Signing you in…</p></div>;

  const chatAllowed = row ? viewer.sees_chat_embed : viewer.sees_scout_tab;
  const companyLabel = row?.account || hint.name || hint.domain;
  const book = bookQuery.data;
  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const next = neighbourTab(event.key, tab);
    if (!next) return;
    event.preventDefault();
    setTab(next);
    document.getElementById(`ext-tab-${next}`)?.focus();
  };

  return (
    <div className="side on extembed">
      <header className="extbar">
        <div className="extcontextrow">
          <p className="extcontext" title={context?.url}>{contextLine(context)}</p>
          {meQuery.data?.preview ? <span className="extchip warn" title="Preview deployment: writes are held back">Preview</span> : null}
          <button type="button" className="extlink" title="Read the open tab again"
            onClick={() => postToExtension({ type: 'scout:read-page' })}>Read page</button>
        </div>
        <form className="exttype" onSubmit={event => { event.preventDefault(); setTyped(typedDraft); setTab('company'); }}>
          <input value={typedDraft} onChange={event => setTypedDraft(event.target.value)}
            placeholder={context && !context.read_ok ? "Couldn't read this page. Type the company" : 'Another company or website'}
            aria-label="Type a company name or website" />
          <button type="submit" className="btn">Look up</button>
        </form>
      </header>

      {book ? <AccountStrip row={row} hint={hint} viewer={viewer} toolSite={toolSite} /> : null}

      <div className="accounttabs" role="tablist" aria-label="Scout panel">
        {PANEL_TABS.map(name => (
          <button key={name} type="button" role="tab" id={`ext-tab-${name}`} aria-controls={`ext-panel-${name}`}
            aria-selected={tab === name} tabIndex={tab === name ? 0 : -1}
            onClick={() => setTab(name)} onKeyDown={onTabKey}>
            {TAB_LABELS[name]}
          </button>
        ))}
      </div>

      <div className="sidebody">
        {bookQuery.isPending ? <p className="extnote">Loading the book…</p> : null}
        {bookQuery.error ? <p className="extnote bad">{panelMessage(bookQuery.error, 'Could not load the book.')}</p> : null}

        {/* Unkeyed, so a book that loads late never remounts PersonTab or ReplyTab mid-enrichment
            or mid-draft. Only the account sections below reset per account (their own key). */}
        <CollapseProvider>
          <section className="accounttabpanel" role="tabpanel" id="ext-panel-scout" aria-labelledby="ext-tab-scout"
            hidden={tab !== 'scout'}>
            {/* Mounted on the first visit, then kept: the composer sizes itself on mount, and a
                hidden panel measures zero. */}
            {chatAllowed && scoutVisited ? (
              <div className="extchat">
                <ChatPanel
                  key={row?.id || resolvedDomain || hint.name || 'none'}
                  row={row ?? undefined}
                  conversationId={row ? undefined : conversationFor(resolvedDomain || hint.name || '')}
                  turnView={turnViewFor(context, resolvedDomain)}
                  starters={startersFor(context, companyLabel)}
                />
              </div>
            ) : null}
            {!chatAllowed ? <p className="tabempty">Scout chat is not switched on for your role yet.</p> : null}
          </section>

          <section className="accounttabpanel" role="tabpanel" id="ext-panel-company" aria-labelledby="ext-tab-company"
            hidden={tab !== 'company'}>
            {book ? (
              <CollapseProvider key={row?.id || 'none'}>
                <CompanySection row={row} hint={hint} />
              </CollapseProvider>
            ) : null}
          </section>

          <section className="accounttabpanel" role="tabpanel" id="ext-panel-outreach" aria-labelledby="ext-tab-outreach"
            hidden={tab !== 'outreach'}>
            {isPersonPage(context)
              ? <PersonTab context={context} domain={personDomain} viewer={viewer} onListed={onPersonListed} onAnswer={setPersonAnswer} />
              : null}
            {context?.kind === 'gmail_thread'
              ? <ReplyTab context={context} onReread={() => postToExtension({ type: 'scout:read-page' })} />
              : null}
            {personBlock ? <p className="tabempty">{personBlock}</p> : null}
            {row && book && !personBlock ? (
              <CollapseProvider key={row.id}>
                <OutreachAccount row={row} book={book} active={tab === 'outreach'}
                  onOpenScout={() => setTab('scout')}
                  onContactEnriched={onContactEnriched} />
              </CollapseProvider>
            ) : null}
            {!row && !isPersonPage(context) && context?.kind !== 'gmail_thread'
              ? <p className="tabempty">{toolSite ? `${TOOL_SITE_LINE}.` : 'Not on the book. Request it to work its people here.'}</p>
              : null}
          </section>

          <section className="accounttabpanel" role="tabpanel" id="ext-panel-research" aria-labelledby="ext-tab-research"
            hidden={tab !== 'research'}>
            {row ? <CollapseProvider key={row.id}><ResearchSection row={row} today={book?.today ?? ''} /></CollapseProvider> : (
              <p className="tabempty">
                No research on file for {companyLabel || 'this company'}.{' '}
                <button type="button" className="extlink" onClick={() => setTab('scout')}>Ask Scout</button>
              </p>
            )}
          </section>
        </CollapseProvider>
      </div>
    </div>
  );
}
